import {
  createBrowserRouter,
  RouterProvider,
  Routes,
  Route,
  Navigate,
  useParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./config/firebase";
import { AuthGuard } from "./views/layout/AuthGuard";
import { HelpSessionView } from "./views/help/HelpSessionView";
import { createHelpSessionApi, deleteHelpSessionApi } from "./api/helpApi";
import { safeReturnTo } from "./utils/helpSite";
import { ResellerGuard } from "./views/layout/ResellerGuard";
import { LoginView } from "./views/LoginView";
import { StoreSelector } from "./views/StoreSelector";
import { StoreLayout } from "./views/layout/StoreLayout";
import { StoreOverview } from "./views/StoreOverview";
import { StoreSettingsLayout } from "./views/storeSettings/StoreSettingsLayout";
import { GeneralSettingsView } from "./views/storeSettings/GeneralSettingsView";
import { StockValuationSettingsView } from "./views/storeSettings/StockValuationSettingsView";
import { ApiClientsSettingsView } from "./views/storeSettings/ApiClientsSettingsView";
import { WebhooksSettingsView } from "./views/storeSettings/WebhooksSettingsView";
import { StockView } from "./views/stock/StockView";
import { ResellerLayout } from "./views/layout/ResellerLayout";
import { ResellerOverview } from "./views/ResellerOverview";
import { ResellerStoresView } from "./views/ResellerStoresView";
import { ProductsView } from "./views/ProductsView";
import { ProductImportView } from "./views/ProductImportView";
import { ReceiptsView } from "./views/ReceiptsView";
import { IntegrationsView } from "./views/IntegrationsView";
import { PaymentMethodsView } from "./views/paymentMethods/PaymentMethodsView";
import { EditPaymentMethodView } from "./views/paymentMethods/EditPaymentMethodView";
import { VivaTransactionsView } from "./views/paymentMethods/viva/VivaTransactionsView";
import { DevicesView } from "./views/DevicesView";
import { BundlesView } from "./views/BundlesView";
import { ApiKeysView } from "./views/ApiKeysView";
import { UsersView } from "./views/UsersView";
import { AdminLayout } from "./views/layout/AdminLayout";
import { AvailableBillingItemsView } from "./views/AvailableBillingItemsView";
import { AdminDashboardView } from "./views/AdminDashboardView";
import { StoresView } from "./views/StoresView";
import { StoreCopyView } from "./views/StoreCopyView";
import { ChainsView } from "./views/ChainsView";
import { ResellersView } from "./views/ResellersView";
import { ResellerUsersView } from "./views/ResellerUsersView";
import { ResellerAvailableBillingItemsView } from "./views/ResellerAvailableBillingItemsView";
import { ResellerInvoicingView } from "./views/ResellerInvoicingView";
import { KickbackView } from "./views/KickbackView";
import { AdminResellerInvoicingView } from "./views/AdminResellerInvoicingView";
import { PriceListView } from "./views/PriceListView";
import { ChainLayout } from "./views/layout/ChainLayout";
import { ChainOverview } from "./views/ChainOverview";
import { ChainStoresView } from "./views/ChainStoresView";
import { ChainSettingsView } from "./views/ChainSettingsView";
import { ProductGroupsView } from "./views/ProductGroupsView";
import { ProductGroupFormView } from "./views/ProductGroupFormView";
import { UnitsView } from "./views/UnitsView";
import { UnitFormView } from "./views/UnitFormView";
import { CoursesView } from "./views/CoursesView";
import { CourseFormView } from "./views/CourseFormView";
import { PrepStationsView } from "./views/PrepStationsView";
import { PrepStationFormView } from "./views/PrepStationFormView";
import { ModifiersView } from "./views/ModifiersView";
import { ModifierFormView } from "./views/ModifierFormView";
import { ModifierGroupsView } from "./views/ModifierGroupsView";
import { ModifierGroupFormView } from "./views/ModifierGroupFormView";
import { BookkeepingAccountsView } from "./views/BookkeepingAccountsView";
import { BookkeepingAccountFormView } from "./views/BookkeepingAccountFormView";
import { SuppliersView } from "./views/SuppliersView";
import { SupplierFormView } from "./views/SupplierFormView";
import { OffersView } from "./views/OffersView";
import { OfferFormView } from "./views/OfferFormView";
import { LayoutsView } from "./views/LayoutsView";
import { LayoutFormView } from "./views/LayoutFormView";
import { useGetStoresQuery } from "./query/storesQuery";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import styled from "styled-components";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { Button } from "./components/Button";
import BookkeepingIntegrationView from "./views/bookkeepingIntegration/BookkeepingIntegrationView";
import ShopifyIntegration from "./views/shopifyIntegration/ShopifyIntegration";
import { PublicReceiptView } from "./views/publicReceipt/PublicReceiptView";
import { useTranslation } from "react-i18next";
import { useGetUserQuery } from "./query/usersQuery";
import { NotFoundView } from "./views/NotFoundView";
import { GiftcardsView } from "./views/GiftcardsView";
import { StoreDebtsView } from "./views/storeDebts/StoreDebtsView";
import { StoreDebtDetailView } from "./views/storeDebts/StoreDebtDetailView";
import { StoreDebtSettleView } from "./views/storeDebts/StoreDebtSettleView";
import { GiftcardImportView } from "./views/GiftcardImportView";
import { StoreInvoicingView } from "./views/StoreInvoicingView";
import { CreditNotesView } from "./views/CreditNotesView";
import { StoreFeatureGuard } from "./views/layout/StoreFeatureGuard";
import { ChainModeGuard } from "./views/layout/ChainModeGuard";
import { Features } from "@KassaroAB/shared/types/enums/Features";
import { StatsView } from "./views/statistics/StatsView";
import { StatsProductsView } from "./views/statistics/StatsProductsView";
import { StatsGroupsView } from "./views/statistics/StatsGroupsView";
import { StoreChatSessionsView } from "./views/StoreChatSessionsView";
import { AdminGuard } from "./views/layout/AdminGuard";
import { AdminOrResellerGuard } from "./views/layout/AdminOrResellerGuard";
import { getHomePath } from "./utils/getHomePath";
import { StagingCorner } from "./components/Navbar";

const queryClient = new QueryClient();
const SessionAuthContext = createContext(false);

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  font-size: ${fontSizes.large}px;
  color: ${colors.textMuted};
`;

function StoreSelectorWrapper() {
  const { t } = useTranslation();
  const { data: stores, isLoading, error, refetch } = useGetStoresQuery();
  const [selectedStoreId, setSelectedStoreId] = useState<StoreId | null>(() => {
    const stored = localStorage.getItem("selectedStoreId");
    return stored as StoreId | null;
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (selectedStoreId) {
      navigate(`/stores/${selectedStoreId}`);
    }
    if (stores && stores.length === 1) {
      const storeId = stores[0].id;
      localStorage.setItem("selectedStoreId", storeId);
      navigate(`/stores/${storeId}`);
    }
  }, [stores, selectedStoreId, navigate]);

  const handleSelectStore = (storeId: StoreId) => {
    setSelectedStoreId(storeId);
    localStorage.setItem("selectedStoreId", storeId);
    navigate(`/stores/${storeId}`);
  };

  if (isLoading) {
    return <LoadingContainer>{t("app.loadingStores")}</LoadingContainer>;
  }

  if (error) {
    return (
      <LoadingContainer>
        <div style={{ display: "flex", flexDirection: "column", gap: `${spacing.x3}px`, alignItems: "center" }}>
          <div>{t("app.errorLoadingStores")}</div>
          <Button onClick={() => refetch()}>{t("common.retry")}</Button>
        </div>
      </LoadingContainer>
    );
  }

  if (!stores || stores.length === 0) {
    return <LoadingContainer>{t("app.noStoresAvailable")}</LoadingContainer>;
  }

  return <StoreSelector stores={stores} onSelectStore={handleSelectStore} />;
}

function StoreLayoutWrapper() {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const selectedStoreId = (storeId as StoreId) || null;

  useEffect(() => {
    if (storeId) {
      localStorage.setItem("selectedStoreId", storeId);
    }
  }, [storeId]);

  const handleSelectStore = (newStoreId: StoreId) => {
    localStorage.setItem("selectedStoreId", newStoreId);
    navigate(`/stores/${newStoreId}`);
  };

  if (!selectedStoreId) {
    return <LoadingContainer>{t("common.loading")}</LoadingContainer>;
  }

  return (
    <StoreLayout
      selectedStoreId={selectedStoreId}
      onStoreChange={handleSelectStore}
    />
  );
}

const BaseRoute = () => {
  const { t } = useTranslation();
  const { data: user, isLoading, error } = useGetUserQuery();

  if (isLoading) {
    return <LoadingContainer>{t("common.loading")}</LoadingContainer>;
  }
  if (error) {
    return <LoadingContainer>{t("common.errorLoadingUser")}</LoadingContainer>;
  }
  if (!user) {
    return null;
  }

  return <Navigate to={getHomePath(user)} replace />;
};

const catalogCollectionRoutes = (
  <>
    <Route
      path="productGroups"
      element={<ProductGroupsView />}
    />
    <Route
      path="productGroups/:itemId"
      element={<ProductGroupFormView />}
    />
    <Route
      path="units"
      element={<UnitsView />}
    />
    <Route
      path="units/:itemId"
      element={<UnitFormView />}
    />
    <Route
      path="courses"
      element={<CoursesView />}
    />
    <Route
      path="courses/:itemId"
      element={<CourseFormView />}
    />
    <Route
      path="prepStations"
      element={<PrepStationsView />}
    />
    <Route
      path="prepStations/:itemId"
      element={<PrepStationFormView />}
    />
    <Route
      path="modifiers"
      element={<ModifiersView />}
    />
    <Route
      path="modifiers/:itemId"
      element={<ModifierFormView />}
    />
    <Route
      path="modifierGroups"
      element={<ModifierGroupsView />}
    />
    <Route
      path="modifierGroups/:itemId"
      element={<ModifierGroupFormView />}
    />
    <Route
      path="bookkeepingAccounts"
      element={<BookkeepingAccountsView />}
    />
    <Route
      path="bookkeepingAccounts/:itemId"
      element={<BookkeepingAccountFormView />}
    />
    <Route
      path="suppliers"
      element={<SuppliersView />}
    />
    <Route
      path="suppliers/:itemId"
      element={<SupplierFormView />}
    />
    <Route
      path="offers"
      element={<OffersView />}
    />
    <Route
      path="offers/:itemId"
      element={<OfferFormView />}
    />
    <Route
      path="layouts"
      element={<LayoutsView />}
    />
    <Route
      path="layouts/:itemId"
      element={<LayoutFormView />}
    />
  </>
);

function LoginRedirect() {
  const [searchParams] = useSearchParams();
  return <Navigate to={safeReturnTo(searchParams.get("returnTo"))} replace />;
}

function AppRoutes() {
  const isAuthenticated = useContext(SessionAuthContext);

  return (
    <Routes>
          <Route
            path="/login"
            element={
              isAuthenticated ? <LoginRedirect /> : <LoginView />
            }
          />
          <Route
            path="/bookkeeping/integration/:integrationType"
            element={
              <BookkeepingIntegrationView />
            }
          />
          <Route
            path="/shopify/integration"
            element={
              <ShopifyIntegration />
            }
          />
          <Route
            path="/help-session"
            element={
              <AuthGuard>
                <HelpSessionView />
              </AuthGuard>
            }
          />
          <Route
            path="/receipt/:encoded"
            element={
              <PublicReceiptView />
            }
          />
          <Route
            path="/admin"
            element={
              <AdminGuard>
                <AdminLayout />
              </AdminGuard>
            }
          >
            <Route
              index
              element={<AdminDashboardView />}
            />
            <Route
              path="stores"
              element={<StoresView />}
            />
            <Route
              path="store-copy"
              element={<StoreCopyView />}
            />
            <Route
              path="availableBillingItems"
              element={<AvailableBillingItemsView />}
            />
            <Route
              path="bundles"
              element={<BundlesView />}
            />
            <Route
              path="api-keys"
              element={<ApiKeysView />}
            />
            <Route
              path="chains"
              element={<ChainsView />}
            />
            <Route
              path="resellers"
              element={<ResellersView />}
            />
            <Route
              path="kickback"
              element={<KickbackView />}
            />
            <Route
              path="reseller-invoicing"
              element={<AdminResellerInvoicingView />}
            />
          </Route>
          <Route
            path="/chains/:chainId"
            element={
              <AuthGuard>
                <ChainLayout />
              </AuthGuard>
            }
          >
            <Route
              path="overview"
              element={<ChainOverview />}
            />
            <Route
              path="stores"
              element={<ChainStoresView />}
            />
            <Route
              path="statistics"
              element={<StatsView />}
            />
            <Route
              path="statistics/products/:period/:periodId"
              element={<StatsProductsView />}
            />
            <Route
              path="statistics/groups/:period/:periodId"
              element={<StatsGroupsView />}
            />
            <Route
              path="storeDebts"
              element={<StoreDebtsView />}
            />
            <Route
              path="storeDebts/:storeDebtId/settle"
              element={<StoreDebtSettleView />}
            />
            <Route
              path="storeDebts/:storeDebtId"
              element={<StoreDebtDetailView />}
            />
            <Route path="catalog" element={<ChainModeGuard />}>
              <Route
                index
                element={<Navigate to="productGroups" replace />}
              />
              <Route
                path="products"
                element={<ProductsView />}
              />
              <Route
                path="products/import"
                element={<ProductImportView />}
              />
              <Route
                path="giftCards"
                element={<GiftcardsView />}
              />
              {catalogCollectionRoutes}
            </Route>
            <Route
              path="productGroups"
              element={<Navigate to="../catalog/productGroups" replace />}
            />
            <Route
              path="units"
              element={<Navigate to="../catalog/units" replace />}
            />
            <Route
              path="settings"
              element={<ChainSettingsView />}
            />
            <Route
              index
              element={<Navigate to="overview" replace />}
            />
          </Route>
          <Route
            path="/resellers/:resellerId"
            element={
              <ResellerGuard>
                <ResellerLayout />
              </ResellerGuard>
            }
          >
            <Route
              path="overview"
              element={<ResellerOverview />}
            />
            <Route
              path="stores"
              element={<ResellerStoresView />}
            />
            <Route
              path="users"
              element={<ResellerUsersView />}
            />
            <Route
              path="availableBillingItems"
              element={<ResellerAvailableBillingItemsView />}
            />
            <Route
              path="invoicing"
              element={<ResellerInvoicingView />}
            />
            <Route
              path="priceList"
              element={<PriceListView />}
            />
            <Route
              index
              element={<Navigate to="overview" replace />}
            />
          </Route>
          <Route path="/stores" element={<StoreSelectorWrapper />} />
          <Route
            path="/stores/:storeId"
            element={
              <AuthGuard>
                <StoreLayoutWrapper />
              </AuthGuard>
            }
          >
            <Route
              path="overview"
              element={<StoreOverview />}
            />
            <Route
              path="statistics"
              element={<StatsView />}
            />
            <Route
              path="statistics/products/:period/:periodId"
              element={<StatsProductsView />}
            />
            <Route
              path="statistics/groups/:period/:periodId"
              element={<StatsGroupsView />}
            />
            <Route
              path="chat-sessions"
              element={
                <AdminOrResellerGuard>
                  <StoreChatSessionsView />
                </AdminOrResellerGuard>
              }
            />
            <Route path="catalog">
              <Route
                index
                element={<Navigate to="productGroups" replace />}
              />
              <Route
                path="products"
                element={<ProductsView />}
              />
              <Route
                path="products/import"
                element={<ProductImportView />}
              />
              {catalogCollectionRoutes}
            </Route>
            <Route
              path="products"
              element={<Navigate to="catalog/products" replace />}
            />
            <Route
              path="products/import"
              element={<Navigate to="catalog/products/import" replace />}
            />
            <Route
              path="receipts"
              element={<ReceiptsView />}
            />
            <Route
              path="receipts/:receiptId"
              element={<ReceiptsView />}
            />
            <Route
              path="devices"
              element={<DevicesView />}
            />
            <Route
              path="users"
              element={<UsersView />}
            />
            <Route
              path="integrations"
              element={
                <AdminGuard>
                  <IntegrationsView />
                </AdminGuard>
              }
            />
            <Route
              path="paymentMethods"
              element={
                <AdminGuard>
                  <PaymentMethodsView />
                </AdminGuard>
              }
            />
            <Route
              path="paymentMethods/:paymentMethodId"
              element={
                <AdminGuard>
                  <EditPaymentMethodView />
                </AdminGuard>
              }
            />
            <Route
              path="paymentMethods/:paymentMethodId/viva-transactions"
              element={
                <AdminGuard>
                  <VivaTransactionsView />
                </AdminGuard>
              }
            />
            <Route
              path="giftcards"
              element={
                <StoreFeatureGuard feature={Features.GIFT_CARD}>
                  <GiftcardsView />
                </StoreFeatureGuard>
              }
            />
            <Route
              path="giftcards/import"
              element={
                <StoreFeatureGuard feature={Features.GIFT_CARD}>
                  <GiftcardImportView />
                </StoreFeatureGuard>
              }
            />
            <Route
              path="credit-notes"
              element={
                <StoreFeatureGuard feature={Features.CREDIT_NOTE}>
                  <CreditNotesView />
                </StoreFeatureGuard>
              }
            />
            <Route
              path="stock"
              element={
                <StoreFeatureGuard feature={Features.INVENTORY_COSTING}>
                  <StockView />
                </StoreFeatureGuard>
              }
            />
            <Route path="settings" element={<StoreSettingsLayout />}>
              <Route index element={<Navigate to="general" replace />} />
              <Route path="general" element={<GeneralSettingsView />} />
              <Route
                path="stock-valuation"
                element={
                  <StoreFeatureGuard feature={Features.INVENTORY_COSTING}>
                    <StockValuationSettingsView />
                  </StoreFeatureGuard>
                }
              />
              <Route
                path="api"
                element={
                  <StoreFeatureGuard feature={Features.PUBLIC_API}>
                    <ApiClientsSettingsView />
                  </StoreFeatureGuard>
                }
              />
              <Route
                path="webhooks"
                element={
                  <StoreFeatureGuard feature={Features.PUBLIC_API}>
                    <WebhooksSettingsView />
                  </StoreFeatureGuard>
                }
              />
            </Route>
            <Route
              path="invoicing"
              element={
                <AdminGuard>
                  <StoreInvoicingView />
                </AdminGuard>
              }
            />
            <Route
              index
              element={<Navigate to="overview" replace />}
            />
          </Route>
          <Route
            path="/"
            element={
              <AuthGuard>
                <BaseRoute />
              </AuthGuard>
            }
          />
          <Route
            path="*"
            element={<NotFoundView />}
          />
        </Routes>
  );
}

const router = createBrowserRouter([{ path: "*", element: <AppRoutes /> }]);

function App() {
  const { t } = useTranslation();
  const [isInitializing, setIsInitializing] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    let previousUid: string | null = null;
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        queryClient.clear();
      }
      const uid = user?.uid ?? null;
      const signedIn = uid !== null && uid !== previousUid;
      const signedOut = uid === null && previousUid !== null;
      previousUid = uid;
      const syncHelpSession = signedIn
        ? window.location.pathname === "/help-session"
          ? null
          : createHelpSessionApi()
        : signedOut
          ? deleteHelpSessionApi()
          : null;
      syncHelpSession?.catch((error: unknown) => {
        console.warn("Could not sync help session", error);
      });
      setIsAuthenticated(!!user);
      setIsInitializing(false);
    });

    return () => unsubscribe();
  }, []);

  if (isInitializing) {
    return (
      <>
        <StagingCorner />
        <LoadingContainer>{t("common.initializing")}</LoadingContainer>
      </>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <SessionAuthContext.Provider value={isAuthenticated}>
        <StagingCorner />
        <RouterProvider router={router} />
      </SessionAuthContext.Provider>
    </QueryClientProvider>
  );
}

export default App;
